'use client';

import { c as _c } from "react/compiler-runtime";
import { useAnchoredPosition } from '@/hooks/use-anchored-position';
import { useCurrentLocale } from '@/hooks/use-current-locale';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import type { Locale, StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import { fitToVisualViewport } from '@/utils/fit-to-visual-viewport';
import { cn } from '@/utils/tailwindcss-override';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { PanInfo } from 'framer-motion';
import { motion, useDragControls, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import type React from 'react';
import { useRef, type RefObject } from 'react';
const closeLabel: StaticTranslationString = {
  de: 'Schliessen',
  en: 'Close',
  fr: 'Fermer'
};

/** A popup fades in where it is; a sheet comes up from below. */
const motionFrom = (popup: boolean): {
  opacity?: number;
  y: number | string;
} => popup ? {
  opacity: 0,
  y: -4
} : {
  y: '100%'
};
const motionDuration = (popup: boolean): number => popup ? 0.12 : 0.24;

/** how far, or how fast, the sheet has to be pulled down before it lets go */
const CLOSE_OFFSET = 96;
const CLOSE_VELOCITY = 500;

/**
 * A sheet that slides up from the bottom, for a choice or a set of filters that belongs to the
 * page underneath: the page stays in sight, dimmed. It closes with its ✕, a tap on the dimmed
 * page, Escape, or by pulling it down at its grabber. Focus stays inside while it is open and
 * returns to what opened it.
 *
 * Given an `anchor`, the same sheet opens on a desktop as a popup under that element, the
 * button that opened it, with the same title, content and footer; on a phone it stays a sheet.
 * The one component for both, so a choice reads alike on every screen.
 */
export const BottomSheet: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** a ✕ beside the title; a sheet that ends in its own "Abbrechen" can leave it out */
  showClose?: boolean;
  /** stays at the foot of the sheet while its content scrolls, e.g. the action it ends in */
  footer?: React.ReactNode;
  /** before the title, e.g. the avatar of the chat a sheet of actions is about */
  leading?: React.ReactNode;
  /** a smaller line under the title, e.g. "Gruppe · stummgeschaltet" */
  description?: string;
  /**
   * names the sheet for screen readers only, where its content says what it is about, e.g. the
   * message a sheet of message actions shows at its top
   */
  hideTitle?: boolean;
  /** On a desktop, open as a popup under this element, usually the button that opens it. */
  anchor?: RefObject<HTMLElement | null>;
  /**
   * keeps the sheet at its full height whatever its content, for content that changes while the
   * sheet is open, like a search that filters a list or tabs, so the sheet does not jump with it
   */
  fullHeight?: boolean;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(74);
  const {
    open,
    onOpenChange,
    title,
    showClose: t1,
    footer,
    leading,
    description,
    hideTitle: t2,
    anchor,
    fullHeight: t3,
    children
  } = t0;
  const showClose = t1 === undefined ? true : t1;
  const hideTitle = t2 === undefined ? false : t2;
  const fullHeight = t3 === undefined ? false : t3;
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const isDesktop = useIsDesktop();
  const popup = anchor !== undefined && isDesktop;
  const position = useAnchoredPosition(popup ? anchor : undefined, open);
  const dragControls = useDragControls();
  const reduceMotion = useReducedMotion() ?? false;
  const openerReference = useRef(undefined);
  const closedByPointerReference = useRef(false);
  let t4;
  if ($[0] !== onOpenChange) {
    t4 = (_, info) => {
      if (info.offset.y > CLOSE_OFFSET || info.velocity.y > CLOSE_VELOCITY) {
        onOpenChange(false);
      }
    };
    $[0] = onOpenChange;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const onDragEnd = t4;
  const t5 = popup ? "bg-transparent" : "bg-green-900/40";
  let t6;
  if ($[2] !== t5) {
    t6 = cn("fixed inset-0 z-[200]", t5);
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== t6) {
    t7 = <DialogPrimitive.Overlay className={t6} />;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== description) {
    t8 = description === undefined ? {
      "aria-describedby": undefined
    } : {};
    $[6] = description;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      closedByPointerReference.current = false;
      const opener = document.activeElement;
      openerReference.current = opener instanceof HTMLElement && opener !== document.body ? opener : undefined;
    };
    t10 = event => {
      const opener_0 = openerReference.current;
      if (opener_0?.isConnected !== true) {
        return;
      }
      event.preventDefault();
      const options = {
        preventScroll: true,
        focusVisible: !closedByPointerReference.current
      };
      opener_0.focus(options);
    };
    t11 = () => {
      closedByPointerReference.current = true;
    };
    t12 = () => {
      closedByPointerReference.current = true;
    };
    t13 = () => {
      closedByPointerReference.current = false;
    };
    $[8] = t10;
    $[9] = t11;
    $[10] = t12;
    $[11] = t13;
    $[12] = t9;
  } else {
    t10 = $[8];
    t11 = $[9];
    t12 = $[10];
    t13 = $[11];
    t9 = $[12];
  }
  const t14 = popup ? "border-line w-[min(30rem,calc(100vw-1rem))] rounded-[14px] border pt-3 shadow-xl" : "inset-x-0 bottom-[var(--visual-viewport-bottom,0px)] mx-auto max-h-[calc(var(--visual-viewport-height,100dvh)*0.9)] w-full max-w-xl rounded-t-[24px] pb-[env(safe-area-inset-bottom)]";
  const t15 = !popup && fullHeight && "h-[calc(var(--visual-viewport-height,100dvh)*0.9)]";
  const t16 = popup && position === undefined && "invisible";
  let t17;
  if ($[13] !== t14 || $[14] !== t15 || $[15] !== t16) {
    t17 = cn("fixed z-[201] flex flex-col bg-white focus:outline-none", t14, t15, t16);
    $[13] = t14;
    $[14] = t15;
    $[15] = t16;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] !== popup || $[18] !== position) {
    t18 = popup && position !== undefined ? {
      top: position.top,
      bottom: position.bottom,
      right: position.right,
      maxHeight: position.maxHeight
    } : {};
    $[17] = popup;
    $[18] = position;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== popup || $[21] !== reduceMotion) {
    t19 = reduceMotion ? false : motionFrom(popup);
    $[20] = popup;
    $[21] = reduceMotion;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== popup) {
    t20 = popup ? {
      opacity: 1,
      y: 0
    } : {
      y: 0
    };
    $[23] = popup;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== popup || $[26] !== reduceMotion) {
    t21 = reduceMotion ? 0 : motionDuration(popup);
    $[25] = popup;
    $[26] = reduceMotion;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== t21) {
    t22 = {
      type: "tween",
      duration: t21,
      ease: "easeOut"
    };
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  const t23 = popup ? false : "y";
  let t24;
  let t25;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      top: 0,
      bottom: 0
    };
    t25 = {
      top: 0,
      bottom: 0.6
    };
    $[30] = t24;
    $[31] = t25;
  } else {
    t24 = $[30];
    t25 = $[31];
  }
  let t26;
  if ($[32] !== dragControls || $[33] !== popup) {
    t26 = event_0 => {
      if (!popup) {
        dragControls.start(event_0);
      }
    };
    $[32] = dragControls;
    $[33] = popup;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] !== popup) {
    t27 = !popup && <div className="flex justify-center pt-2 pb-1"><span aria-hidden="true" className="bg-rail h-1 w-9 rounded-full" /></div>;
    $[35] = popup;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  let t28;
  if ($[37] !== description || $[38] !== hideTitle || $[39] !== leading || $[40] !== locale || $[41] !== showClose || $[42] !== title) {
    t28 = hideTitle ? <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title> : <div className="flex min-h-11 items-center gap-3 px-5 pt-1 pb-2">{leading}<div className="min-w-0 flex-1"><DialogPrimitive.Title className={description === undefined && leading === undefined ? "font-heading text-heading text-[22px] leading-7 font-extrabold" : "font-heading text-heading truncate text-[17px] leading-6 font-bold"}>{title}</DialogPrimitive.Title>{description !== undefined && <DialogPrimitive.Description className="truncate text-[13px] text-green-700">{description}</DialogPrimitive.Description>}</div>{showClose && <DialogPrimitive.Close aria-label={closeLabel[locale]} className="focus-visible:focus-surface bg-quiet hover:bg-chip flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-green-900 transition-colors"><X className="h-5 w-5" aria-hidden="true" /></DialogPrimitive.Close>}</div>;
    $[37] = description;
    $[38] = hideTitle;
    $[39] = leading;
    $[40] = locale;
    $[41] = showClose;
    $[42] = title;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t26 || $[45] !== t27 || $[46] !== t28) {
    t29 = <div className="shrink-0 touch-none" onPointerDown={t26}>{t27}{t28}</div>;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== children) {
    t30 = <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5">{children}</div>;
    $[48] = children;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== footer) {
    t31 = footer !== undefined && <div className="border-line shrink-0 border-t px-5 py-3">{footer}</div>;
    $[50] = footer;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== dragControls || $[53] !== onDragEnd || $[54] !== t17 || $[55] !== t18 || $[56] !== t19 || $[57] !== t20 || $[58] !== t22 || $[59] !== t23 || $[60] !== t29 || $[61] !== t30 || $[62] !== t31) {
    t32 = <motion.div ref={fitToVisualViewport} className={t17} style={t18} initial={t19} animate={t20} transition={t22} drag={t23} dragListener={false} dragControls={dragControls} dragConstraints={t24} dragElastic={t25} onDragEnd={onDragEnd}>{t29}{t30}{t31}</motion.div>;
    $[52] = dragControls;
    $[53] = onDragEnd;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t22;
    $[59] = t23;
    $[60] = t29;
    $[61] = t30;
    $[62] = t31;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  if ($[64] !== t32 || $[65] !== t8) {
    t33 = <DialogPrimitive.Content asChild={true} {...t8} onOpenAutoFocus={t9} onCloseAutoFocus={t10} onPointerDownOutside={t11} onPointerDownCapture={t12} onKeyDownCapture={t13}>{t32}</DialogPrimitive.Content>;
    $[64] = t32;
    $[65] = t8;
    $[66] = t33;
  } else {
    t33 = $[66];
  }
  let t34;
  if ($[67] !== t33 || $[68] !== t7) {
    t34 = <DialogPrimitive.Portal>{t7}{t33}</DialogPrimitive.Portal>;
    $[67] = t33;
    $[68] = t7;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== onOpenChange || $[71] !== open || $[72] !== t34) {
    t35 = <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>{t34}</DialogPrimitive.Root>;
    $[70] = onOpenChange;
    $[71] = open;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  return t35;
};